"use client";

import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { motion } from "motion/react";
import { cn } from "@/lib/utils";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";

export type TypewriterWord = {
  text: string;
  className?: string;
};

/**
 * One-shot: words appear in sequence with a soft blur fade (Aceternity-style).
 */
export function TypewriterEffectSmooth({
  words,
  className,
  cursorClassName,
}: {
  words: TypewriterWord[];
  className?: string;
  cursorClassName?: string;
}) {
  return (
    <div
      className={cn("inline-flex flex-wrap items-baseline gap-x-[0.15em]", className)}
    >
      {words.map((word, i) => (
        <motion.span
          key={`${word.text}-${i}`}
          initial={{ opacity: 0, filter: "blur(6px)", y: 6 }}
          animate={{ opacity: 1, filter: "blur(0px)", y: 0 }}
          transition={{
            duration: 0.42,
            delay: i * 0.11,
            ease: [0.25, 1, 0.5, 1],
          }}
          className={cn("inline-block", word.className)}
        >
          {word.text}
        </motion.span>
      ))}
      <motion.span
        aria-hidden
        className={cn(
          "inline-block h-[0.75em] w-[2px] translate-y-[0.12em] bg-[var(--primary)] align-baseline",
          cursorClassName,
        )}
        animate={{ opacity: [1, 0.2, 1] }}
        transition={{ duration: 0.85, repeat: Infinity, ease: "easeInOut" }}
      />
    </div>
  );
}

type TypewriterRotatingPhrasesProps = {
  phrases: readonly string[];
  className?: string;
  /** Extra content after the caret (e.g. a period); included in reserved width when set. */
  trailing?: ReactNode;
  /** Reserve horizontal space for the longest phrase so delete does not shrink layout. */
  reserveMinWidth?: boolean;
  /** Added to longest phrase length in `ch` (caret uses part of this; default 3). */
  minWidthExtraCh?: number;
  typingIntervalMs?: number;
  deletingIntervalMs?: number;
  holdMs?: number;
  betweenMs?: number;
};

/**
 * Cycles phrases: type forward, hold, delete, next. Respects reduced motion.
 */
export function TypewriterRotatingPhrases({
  phrases,
  className,
  trailing,
  reserveMinWidth = false,
  minWidthExtraCh = 3,
  typingIntervalMs = 46,
  deletingIntervalMs = 30,
  holdMs = 2200,
  betweenMs = 320,
}: TypewriterRotatingPhrasesProps) {
  const reduceMotion = usePrefersReducedMotion();
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(0);
  const [deleting, setDeleting] = useState(false);

  const safe = phrases.length > 0;
  const phrase = safe ? phrases[phraseIndex % phrases.length]! : "";

  const maxPhraseChars = useMemo(
    () => phrases.reduce((m, p) => Math.max(m, p.length), 0),
    [phrases],
  );

  const widthStyle =
    reserveMinWidth && maxPhraseChars > 0
      ? ({
          minWidth: `${maxPhraseChars + minWidthExtraCh + (trailing ? 1 : 0)}ch`,
        } as const)
      : undefined;

  useEffect(() => {
    if (reduceMotion || !safe) return;

    let id: ReturnType<typeof setTimeout>;

    if (!deleting) {
      if (visibleCount < phrase.length) {
        id = setTimeout(() => setVisibleCount((c) => c + 1), typingIntervalMs);
      } else {
        id = setTimeout(() => setDeleting(true), holdMs);
      }
    } else if (visibleCount > 0) {
      id = setTimeout(() => setVisibleCount((c) => c - 1), deletingIntervalMs);
    } else {
      id = setTimeout(() => {
        setPhraseIndex((i) => (i + 1) % phrases.length);
        setDeleting(false);
      }, betweenMs);
    }

    return () => clearTimeout(id);
  }, [
    reduceMotion,
    safe,
    phrase,
    phrase.length,
    phrases.length,
    visibleCount,
    deleting,
    typingIntervalMs,
    deletingIntervalMs,
    holdMs,
    betweenMs,
  ]);

  if (!safe) {
    return null;
  }

  if (reduceMotion) {
    return (
      <span
        className={cn("inline-flex items-baseline", className)}
        style={widthStyle}
      >
        {phrases[0]}
        {trailing}
      </span>
    );
  }

  const shown = phrase.slice(0, visibleCount);

  return (
    <span
      className={cn("inline-flex items-baseline", className)}
      style={widthStyle}
    >
      <span className="inline" aria-live="polite">
        {shown}
      </span>
      <motion.span
        aria-hidden
        className="inline-block h-[0.72em] w-[2px] ml-1 translate-y-[0.1em] bg-[var(--primary)] align-baseline"
        animate={{ opacity: [1, 0.2, 1] }}
        transition={{ duration: 0.85, repeat: Infinity, ease: "easeInOut" }}
      />
      {trailing}
    </span>
  );
}
